import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import type { CardDefinitionQueryParams } from "src/app/features/CardImplementation/subFeatures/CardDefinition/services/server/CardDefinitionQueryTypes";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { useCardDefinitionSaveMutation } from "../../services/server/CardDefinitionApi";
import type { CardDefinitionEntity } from "../../types/CardDefinitionEntity";

export const CardDefinitionCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("card-definition");
    const { cardImplementationId } = useParams();
    const [queryConfig, setQueryConfig] = useState<CardDefinitionQueryParams>({} as CardDefinitionQueryParams);

    useEffect(() => {
        if (cardImplementationId) {
            setQueryConfig({ cardImplementationId });
        }
    }, [cardImplementationId]);

    const [formData, setFormData] = useState<CardDefinitionEntity>({} as CardDefinitionEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const completeSubmit = useFormSubmitCompletion(
        formAction,
        `${BASE_PATH}/${ResourceName.CARD_IMPLEMENTATIONS}/${FORM_ACTION.EDIT}/${cardImplementationId}`,
    );
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardDefinitionSaveMutation();

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) return;

            const result = await saveFormData({ cardDefinitionFormData: formData, cardDefinitionQuery: queryConfig });

            if (!("error" in result)) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", {
                        id: result.data.id,
                        resource: "CardDefinition",
                    }),
                    `${BASE_PATH}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, formData, completeSubmit, cardImplementationId, queryConfig],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardDefinitionEntity>
                    name="CardDefinitionCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
